<template>
    <div class="py-4 w-100">
      <!-- Tabs Navigation -->
      <ul class="nav nav-tabs mb-3 w-100" role="tablist">
        <li class="nav-item" v-for="tab in tabs" :key="tab.name">
          <a
            class="nav-link"
            :class="{active: activeTab === tab.name}"
            href="#"
            @click.prevent="activeTab = tab.name"
          >
            <i :class="tab.icon" class="mr-1"></i>
            {{ tab.label }}
          </a>
        </li>
      </ul>
  
      <!-- Tab Content -->
      <div class="tab-content rounded shadow-sm bg-white p-4">
        <!-- Overview -->
        <div v-show="activeTab === 'overview'">
          <h5 class="mb-3 font-weight-bold">Monthly Traffic Trend {{ barChartData.year }}</h5>
          <traffic-bar-chart :labels="barChartData.labels" :datasets="barChartData.datasets"/>
        </div>

        <!-- Daily Activity -->
        <div v-show="activeTab === 'daily'">
          <h5 class="mb-3 font-weight-bold">Daily In/Out Activity</h5>
          <div class="d-flex align-items-center mb-3">
            <div class="btn-group" role="group" aria-label="Basic example">
              <button
                type="button"
                :class="['btn', localSelectedPeriod === 6 ? 'btn-primary' : 'btn-secondary']"
                @click="selectPeriod(6)">
                6 h
              </button>
              <button
                type="button"
                :class="['btn', localSelectedPeriod === 12 ? 'btn-primary' : 'btn-secondary']"
                @click="selectPeriod(12)">
                12 h
              </button>
              <button
                type="button"
                :class="['btn', localSelectedPeriod === 24 ? 'btn-primary' : 'btn-secondary']"
                @click="selectPeriod(24)">
                24 h
              </button>
            </div>
            <span v-if="lineLoading || (lineChartData.datasets || []).length === 0">
              &nbsp;&nbsp;&nbsp;
              <span class="text-primary">
                <b-spinner small label="Loading..." type="grow"></b-spinner>
              </span>
              <span class="pl-2">loading ..</span>
            </span>
          </div>
          <traffic-line-chart :labels="lineChartData.labels" :datasets="lineChartData.datasets"/>
        </div>

        <!-- Heatmap -->
        <div v-show="activeTab === 'heatmap'" style="overflow-x:auto; max-width:100%;">
          <h5 class="mb-3 font-weight-bold">Peta Panas Aktivitas (Minggu Ini)</h5>

          <div class="d-flex flex-column mb-3">
            <div class="box d-flex mb-2" style="gap:10px;">
              <div class="input-group input-group-sm flex-nowrap">
                <div class="input-group-prepend">
                  <span class="input-group-text" id="inputGroup-sizing-sm">Start</span>
                </div>
                <input :value="heatMapData.periods.start" type="text" class="text-center form-control" aria-label="Sizing example input" aria-describedby="inputGroup-sizing-sm" disabled>
              </div>
              <div class="input-group input-group-sm flex-nowrap">
                <div class="input-group-prepend">
                  <span class="input-group-text" id="inputGroup-sizing-sm">End</span>
                </div>
                <input :value="heatMapData.periods.end" type="text" class="text-center form-control" aria-label="Sizing example input" aria-describedby="inputGroup-sizing-sm" disabled>
              </div>
            </div>
  
            <div class="box mb-3 btn-group btn-group-sm" role="group" aria-label="Basic example">
              <button 
                type="button" 
                class="btn btn-secondary"
                @click="changeMark('prev')">
                Prev
              </button>
              <button 
                type="button" 
                class="btn btn-secondary"
                @click="changeMark('next')">
                Next
              </button>

              <!-- here -->
              <span v-if="lineLoading || (heatMapData.data || []).length === 0">
                &nbsp;&nbsp;&nbsp;
                <span class="text-primary">
                  <b-spinner small label="Loading..." type="grow"></b-spinner>
                </span>
                <span class="pl-2">loading ..</span>
              </span>
            </div>

          </div>

          <heatmap-grid :data="heatMapData.data" />
        </div>

        <!-- Logs -->
        <div v-show="activeTab === 'logs'">
          <h5 class="mb-3 font-weight-bold">Today's Activity Log</h5>
          <activity-log-table :logs="activityLogData.logs" />
        </div>
        <div v-show="activeTab === 'frequentlyTrucking'">
          <h5 class="mb-3 font-weight-bold">Top 10 Trucking of The Month</h5>
          <frequently-trucking-table :trucking="frequentlyTrucking.trucking" />
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import TrafficBarChart from './TrafficBarChart.vue'
  import TrafficLineChart from './TrafficLineChart.vue'
  import HeatmapGrid from './HeatmapGrid.vue'
  import ActivityLogTable from './ActivityLogTable.vue'
  import FrequentlyTruckingTable from './FrequentlyTruckingTable.vue'
  
  export default {
    name: 'DashboardTabs',
    components: {
      TrafficBarChart,
      TrafficLineChart,
      HeatmapGrid,
      ActivityLogTable,
      FrequentlyTruckingTable
    },
    props: {
      // allow parent to control the currently selected period
      selectedPeriod: {
        type: Number,
        required: false,
        default: 6
      },
      barChartData: {
          type: Object,
          required: true,
          default: () => ({ labels: [], datasets: [] })
      },
      lineChartData: {
          type: Object,
          required: true,
          default: () => ({ labels: [], datasets: [] })
      },
      // explicit loading flag for the line chart (parent should toggle this)
      lineLoading: {
        type: Boolean,
        required: false,
        default: false
      },
      heatMapData: {
          type: Object,
          required: true,
          default: () => ({
            periods:{
              start:'', 
              end:''
            }, 
            data: [] 
          })
      },
      activityLogData: {
          type: Object,
          required: true,
          default: () => ({ logs: [] })
      },
      frequentlyTrucking: {
        type: Object,
        required: true,
        default: () => ({ trucking: [] })
      }
    },
    data() {
      return {
        activeTab: 'overview',
        heatmapMark: 0,
        // local selected period (hours) for Daily Activity — kept in sync with parent via prop
        localSelectedPeriod: this.selectedPeriod,
        tabs: [
          { name: 'overview', label: 'Overview', icon: 'fa fa-chart-bar' },
          { name: 'daily', label: 'Daily Activity', icon: 'fa fa-calendar' },
          { name: 'heatmap', label: 'Traffic Heatmap', icon: 'fa fa-clock' },
          { name: 'logs', label: 'Activity Logs', icon: 'fa fa-file' },
          { name: 'frequentlyTrucking', label: 'Top 10 Trucking', icon: 'fa fa-users' },
        ]
      }
    },
    watch: {
      // when parent updates the prop, keep localSelectedPeriod in sync
      selectedPeriod(newVal) {
        this.localSelectedPeriod = newVal;
      },
      changeMark(mark) {
        this.heatmapMark = mark;
      }
    },
    methods: {
      selectPeriod(period) {
        // update local selectedPeriod so buttons toggle class, then emit to parent
        this.localSelectedPeriod = period;
        this.$emit('period-selected', period);
      },
      changeMark(mark){
        if(mark === 'next'){
          this.heatmapMark -= 1;
        }
        
        if(mark === 'prev'){
          this.heatmapMark += 1;
        }

        if(this.heatmapMark < 0){
          this.heatmapMark = 0;
        }

        // console.log('changed mark to:', this.heatmapMark);
        this.$emit('change-heatmap-mark', this.heatmapMark);
      }
    }
  }
  </script>
  
  <style scoped>
  .box { width: 100%; }
  @media (min-width: 746px) {
    .box { width: 40%; }
  }

  .nav-tabs .nav-link.active {
    background: #f5f8fa;
    border-color: #e3e6ec #e3e6ec #fff;
    color: #007bff;
    font-weight: 600;
    box-shadow: 0 2px 8px 0 rgba(33,150,243,.08);
  }
  .tab-content {
    min-height: 350px;
  }
  </style>